import React, { useState } from 'react';
import { Navigate } from 'react-router';
import { ShieldCheck, Sparkles } from 'lucide-react';
import { RETURN_TO_KEY, useAuth } from '../context/AuthContext';
import { getRememberMePreference } from '../services/supabaseClient';

const GoogleMark = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
        <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
        <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.34A9 9 0 0 0 9 18Z" />
        <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.94H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.06l3.01-2.34Z" />
        <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.94l3.01 2.34C4.68 5.16 6.66 3.58 9 3.58Z" />
    </svg>
);

const safeReturnTo = (value) => (
    typeof value === 'string' && value.startsWith('/') && !value.startsWith('//') ? value : '/'
);

const SignIn = ({ returnTo = '/' }) => {
    const {
        configured,
        user,
        loading,
        startupIssue,
        hasProtectedUnsyncedChanges,
        discardUnsyncedChanges,
        signInWithGoogle
    } = useAuth();
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [rememberMe, setRememberMe] = useState(getRememberMePreference);

    if (user && !loading) {
        const storedPath = window.sessionStorage.getItem(RETURN_TO_KEY);
        return <Navigate to={safeReturnTo(storedPath || returnTo)} replace />;
    }

    const handleGoogle = async () => {
        setError('');
        setBusy(true);
        try {
            await signInWithGoogle({ rememberMe, returnTo: safeReturnTo(returnTo) });
            // On success the browser redirects to Google, so nothing renders after.
        } catch (err) {
            setError(err.message || 'Sign in failed. Please try again.');
            setBusy(false);
        }
    };

    return (
        <div className="auth-screen">
            <section className="auth-card">
                <span className="auth-mark"><Sparkles size={22} /></span>
                <p className="shared-kicker"><Sparkles size={14} /> Your private space</p>
                <h1>Welcome to Prayer Guide</h1>
                <p className="auth-sub">
                    Sign in with Google to open your journal, prayers, schedules, and settings.
                </p>

                {configured ? (
                    <>
                        <button className="auth-google" onClick={handleGoogle} disabled={busy}>
                            <GoogleMark /> {busy ? 'Connecting…' : 'Continue with Google'}
                        </button>
                        <label className="auth-remember">
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(event) => setRememberMe(event.target.checked)}
                            />
                            <span>
                                <strong>Keep me signed in</strong>
                                <small>Turn this off on a shared or public device.</small>
                            </span>
                        </label>
                        {error && <p className="auth-error">{error}</p>}
                        {startupIssue && <p className="auth-notice" role="status">{startupIssue}</p>}
                        {hasProtectedUnsyncedChanges ? (
                            <button className="auth-discard" type="button" onClick={discardUnsyncedChanges}>
                                Discard unsaved changes
                            </button>
                        ) : null}
                    </>
                ) : (
                    <div className="auth-notice">
                        <strong>Sign-in configuration is required.</strong>
                        <p>
                            Add your Supabase URL and anon key to <code>.env.local</code> and set up the
                            Google provider. See <code>docs/SUPABASE_SETUP.md</code>.
                        </p>
                    </div>
                )}

                <p className="auth-fineprint">
                    <ShieldCheck size={14} /> Your Google password is never shared with Prayer Guide.
                    Personal app data is loaded only after your account is verified.
                </p>
            </section>
        </div>
    );
};

export default SignIn;
